import user from 'js/lib/user';

import type { PluginDetectionConfiguration } from 'bundles/academic-integrity/components/plugin-detector/detection-helpers';
import AcademicIntegrityExperiments from 'bundles/epic/clients/AcademicIntegrity';

export const excludeLearnerFromIdVerificationFingerprint = (): boolean => {
  const userId = user.get().id?.toString();
  const exclusionList = AcademicIntegrityExperiments.get('idVerificationFingerprintExclusionList') ?? [];
  if (exclusionList.length > 0) {
    return exclusionList.includes(userId);
  }
  return false;
};

export const isIdVerificationFingerprintEnabled = (courseId: string): boolean => {
  if (excludeLearnerFromIdVerificationFingerprint()) {
    return false;
  }
  // eslint-disable-next-line camelcase
  return AcademicIntegrityExperiments.get('enableIdVerificationFingerprint', { course_id: courseId }) ?? false;
};

export const isVivaExamEnabled = (courseId: string, itemId: string): boolean => {
  const enabledAssignments = AcademicIntegrityExperiments.get('enableVivaExam') ?? {};
  if (enabledAssignments[courseId] && enabledAssignments[courseId][itemId]) {
    return true;
  }
  return false;
};

export const isAutoProctorEnabled = (courseId: string): boolean => {
  // eslint-disable-next-line camelcase
  return AcademicIntegrityExperiments.get('enableAutoProctor', { course_id: courseId }) ?? false;
};

export const isProctoringResultsAppEnabled = (): boolean => {
  return AcademicIntegrityExperiments.get('enableProctoringResultsAppEnabled') ?? false;
};

export const isVivaExamFeedbackEnabled = (courseId?: string): boolean => {
  if (courseId) {
    // eslint-disable-next-line camelcase
    return AcademicIntegrityExperiments.get('enableVivaExamFeedback', { course_id: courseId }) ?? false;
  }
  return false;
};

export const getPluginConfiguration = (): PluginDetectionConfiguration => {
  return AcademicIntegrityExperiments.get('pluginDetectionConfiguration') ?? { enabled: false };
};

export const isCourseraProctoringExperimentEnabled = (): string => {
  return AcademicIntegrityExperiments.get('enableCourseraProctoringExperiment');
};

export const isCourseInProctoringExperiment = (courseId?: string): boolean => {
  if (courseId) {
    const experimentCourses = AcademicIntegrityExperiments.get('courseraProctoringExperimentCourse') ?? [];
    return experimentCourses.includes(courseId);
  }
  return false;
};

export const isToolsBasedChallengeEnabled = (courseId: string): boolean => {
  return AcademicIntegrityExperiments.get('enableToolsBasedChallengeV2', { course_id: courseId });
};

// https://tools.coursera.org/epic/experiment/0-S00JSeEfCAfhJYNo68EQ
export const isCourseProfilesFeaturesV2Enabled = (): boolean => {
  return AcademicIntegrityExperiments.get('enableCourseProfilesFeaturesV2') ?? false;
};

export const isCourseProfilesRedesignEnabled = (): boolean => {
  return AcademicIntegrityExperiments.get('enableCourseProfilesRedesign') ?? false;
};

export const isHonorCodeAgreementV2Enabled = (): boolean => {
  return AcademicIntegrityExperiments.get('enableHonorCodeAgreementV2') ?? false;
};

export const isAiBlockerEnabled = (courseId: string): boolean => {
  return AcademicIntegrityExperiments.get('enableAiBlocker', { course_id: courseId }) ?? false;
};

export const isPlagiarismDetectionForAiGradedQuestionsEnabled = (courseId: string): boolean => {
  return (
    AcademicIntegrityExperiments.get('enablePlagiarismDetectionForAiGradedQuestions', { course_id: courseId }) ?? false
  );
};

/**
 * Check if HUMAN Security sensor is enabled for bot detection on assignment pages.
 * This controls loading the HUMAN (PerimeterX) behavioral analysis script.
 */
export const isHumanSensorEnabled = (): boolean => {
  return AcademicIntegrityExperiments.get('enableHumanSensor') ?? false;
};

export const isDataDomeBotDetectionEnabled = (): boolean => {
  return AcademicIntegrityExperiments.get('enableDataDomeBotDetection') ?? false;
};

export const isGradedLabsEnabled = (): boolean => {
  return AcademicIntegrityExperiments.get('enableGradedLabs') ?? false;
};
